import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = import.meta.env.VITE_API_URL;

if (!API_URL) {
  throw new Error('VITE_API_URL environment variable is not set');
}

interface ApiError {
  error: string;
  message: string;
}

class ApiClient {
  private async getAuthToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      return idToken || null;
    } catch (error) {
      console.error('Failed to get auth token:', error);
      return null;
    }
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const token = await this.getAuthToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (options.headers) {
      Object.assign(headers, options.headers);
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}${path}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      let errorData: ApiError;
      try {
        errorData = await response.json();
      } catch {
        errorData = {
          error: 'Unknown Error',
          message: `HTTP ${response.status}: ${response.statusText}`,
        };
      }
      throw new Error(errorData.message || 'Request failed');
    }

    // Handle 204 No Content responses (empty body)
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  async get<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'GET' });
  }

  async post<T>(path: string, data?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(path: string, data: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'DELETE' });
  }

  async patch<T>(path: string, data: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Health check (no auth required)
  async checkHealth(): Promise<{ status: string; timestamp: string }> {
    const response = await fetch(`${API_URL}/health`);
    return response.json();
  }
}

export const apiClient = new ApiClient();

// User API
export interface UserProfile {
  id: string;
  email: string;
  displayName: string | null;
  avatarUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateProfileInput {
  displayName?: string;
  avatarUrl?: string;
}

export const userApi = {
  register: async (displayName?: string): Promise<UserProfile> => {
    return apiClient.post<UserProfile>('/user/register', { displayName });
  },

  getProfile: async (): Promise<UserProfile> => {
    return apiClient.get<UserProfile>('/user/profile');
  },

  updateProfile: async (input: UpdateProfileInput): Promise<UserProfile> => {
    return apiClient.put<UserProfile>('/user/profile', input);
  },
};

// League API
export interface Commissioner {
  cognitoId: string;
  email: string;
  displayName: string | null;
}

export interface League {
  id: string;
  name: string;
  creatorId: string;
  tournamentId: string | null;
  leagueType: 'tournament_team' | 'single_round' | 'interactive';
  budgetPerTeam: number;
  status: 'draft' | 'active' | 'completed';
  inviteCode: string | null;
  memberCount?: number;
  isCreator?: boolean;
  isMember?: boolean;
  isCommissioner?: boolean;
  config?: Record<string, any>;
  rosterLocked?: boolean;
  rosterLockTime?: string | null;
  tournamentDefaultLockTime?: string | null;
  tournamentHashtags?: string[];
  tournamentName?: string | null;
  tournamentFloUrl?: string | null;
  tournamentWrestlestatUrl?: string | null;
  tournamentFinalized?: boolean;
  scoresLastUpdated?: string | null;
  requireTeamApproval?: boolean;
  allowMultipleEntries?: boolean;
  createdAt: string;
  updatedAt: string;
  commissioners?: Commissioner[];
}

export interface LeagueMember {
  id: string;
  userId: string;
  cognitoId?: string;
  email?: string;
  displayName?: string | null;
  teamName: string | null;
  totalPoints: number;
  rank: number | null;
  status: 'approved' | 'pending';
  joinedAt: string;
}

export interface PendingMember {
  id: string;
  userId: string;
  cognitoId: string;
  email: string;
  displayName: string | null;
  teamName: string | null;
  joinedAt: string;
}

export interface LeagueDetails extends League {
  commissioners: Commissioner[];
  members: LeagueMember[];
  pagination?: PaginationMetadata;
  currentUserTeams?: CurrentUserTeam[] | null;
  pendingCount?: number;
}

export interface LeaguePreview {
  id: string;
  name: string;
  budgetPerTeam: number;
  memberCount: number;
  status: 'draft' | 'active' | 'completed';
  requireTeamApproval?: boolean;
  allowMultipleEntries?: boolean;
}

export interface PublicLeague {
  id: string;
  name: string;
  inviteCode: string;
  budgetPerTeam: number;
  status: string;
  allowMultipleEntries: boolean;
  tournamentName: string | null;
  tournamentStartDate: string | null;
  tournamentEndDate: string | null;
  tournamentLocation: string | null;
  memberCount: number;
}

export interface DashboardLeague {
  id: string;
  name: string;
  status: string;
  rosterLocked: boolean;
  rosterLockTime: string | null;
  tournamentId: string | null;
  tournamentName: string | null;
  tournamentStatus: string | null;
  tournamentFinalized: boolean;
  tournamentStartDate: string | null;
  tournamentEndDate: string | null;
  memberCount: number;
  userTeams: Array<{
    memberId: string;
    teamName: string | null;
    rank: number | null;
    totalPoints: number;
    status: 'approved' | 'pending';
  }>;
}

export interface CreateLeagueInput {
  name: string;
  teamName?: string;
  tournamentId?: string;
  leagueType?: 'tournament_team' | 'single_round' | 'interactive';
  budgetPerTeam?: number;
  config?: Record<string, any>;
  rosterLockTime?: string;
}

export interface JoinLeagueInput {
  inviteCode: string;
  teamName?: string;
}

export interface StandingEntry {
  id: string;
  userId: string;
  cognitoId: string;
  email: string;
  displayName: string | null;
  teamName: string | null;
  totalPoints: number;
  totalCost: number;
  joinedAt: string;
  rank: number;
  wrestlers: RosterWrestler[];
}

export interface PaginationMetadata {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface CurrentUserTeam {
  memberId: string;
  teamName: string | null;
  rank: number;
  totalPoints: number;
  status: 'approved' | 'pending';
}

export interface Standings {
  leagueId: string;
  leagueName: string;
  standings: StandingEntry[];
  pagination: PaginationMetadata;
  currentUserTeams: CurrentUserTeam[] | null;
}

export interface StandingsOptions {
  page?: number;
  pageSize?: number;
}

export const leagueApi = {
  create: async (input: CreateLeagueInput): Promise<League> => {
    return apiClient.post<League>('/leagues', input);
  },

  list: async (): Promise<{ leagues: League[] }> => {
    return apiClient.get<{ leagues: League[] }>('/leagues');
  },

  myDashboard: async (): Promise<{ leagues: DashboardLeague[] }> => {
    return apiClient.get<{ leagues: DashboardLeague[] }>('/leagues/my-dashboard');
  },

  get: async (leagueId: string, options?: StandingsOptions): Promise<LeagueDetails> => {
    const params = new URLSearchParams();
    if (options?.page) {
      params.append('page', options.page.toString());
    }
    if (options?.pageSize) {
      params.append('pageSize', options.pageSize.toString());
    }
    const queryString = params.toString();
    const url = queryString ? `/leagues/${leagueId}?${queryString}` : `/leagues/${leagueId}`;
    return apiClient.get<LeagueDetails>(url);
  },

  getPublicLeagues: async (): Promise<PublicLeague[]> => {
    const response = await fetch(`${API_URL}/leagues/public`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error('Failed to fetch public leagues');
    }
    const data = await response.json();
    return data.leagues;
  },

  getByInviteCode: async (inviteCode: string): Promise<LeaguePreview> => {
    // Public endpoint - no auth required
    const response = await fetch(`${API_URL}/leagues/by-invite/${inviteCode.toUpperCase()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch league');
    }

    return response.json();
  },

  getStandings: async (leagueId: string, options?: StandingsOptions): Promise<Standings> => {
    const params = new URLSearchParams();
    if (options?.page) {
      params.append('page', options.page.toString());
    }
    if (options?.pageSize) {
      params.append('pageSize', options.pageSize.toString());
    }
    const queryString = params.toString();
    const url = queryString
      ? `/leagues/${leagueId}/standings?${queryString}`
      : `/leagues/${leagueId}/standings`;
    return apiClient.get<Standings>(url);
  },

  join: async (
    input: JoinLeagueInput
  ): Promise<{ leagueId: string; leagueName: string; status: 'approved' | 'pending' }> => {
    return apiClient.post<{ leagueId: string; leagueName: string; status: 'approved' | 'pending' }>(
      '/leagues/join',
      input
    );
  },

  updateMember: async (
    leagueId: string,
    memberId: string,
    teamName: string
  ): Promise<LeagueMember> => {
    return apiClient.patch<LeagueMember>(`/leagues/${leagueId}/members/${memberId}`, { teamName });
  },

  update: async (
    leagueId: string,
    input: {
      name?: string;
      budgetPerTeam?: number;
      rosterLocked?: boolean;
      rosterLockTime?: string | null;
      requireTeamApproval?: boolean;
      allowMultipleEntries?: boolean;
    }
  ): Promise<League> => {
    return apiClient.patch<League>(`/leagues/${leagueId}`, input);
  },

  delete: async (leagueId: string): Promise<void> => {
    return apiClient.delete(`/leagues/${leagueId}`);
  },

  addCommissioner: async (
    leagueId: string,
    cognitoId: string
  ): Promise<{ commissioners: Commissioner[] }> => {
    return apiClient.post(`/leagues/${leagueId}/commissioners`, { cognitoId });
  },

  removeCommissioner: async (
    leagueId: string,
    cognitoId: string
  ): Promise<{ commissioners: Commissioner[] }> => {
    return apiClient.delete(`/leagues/${leagueId}/commissioners/${cognitoId}`);
  },

  getPendingMembers: async (leagueId: string): Promise<PendingMember[]> => {
    const response = await apiClient.get<{ pendingMembers: PendingMember[] }>(
      `/leagues/${leagueId}/pending-members`
    );
    return response.pendingMembers;
  },

  approveMember: async (leagueId: string, memberId: string): Promise<void> => {
    return apiClient.post<void>(`/leagues/${leagueId}/members/${memberId}/approve`);
  },

  rejectMember: async (leagueId: string, memberId: string): Promise<void> => {
    return apiClient.delete<void>(`/leagues/${leagueId}/members/${memberId}/reject`);
  },

  deleteMember: async (leagueId: string, memberId: string): Promise<void> => {
    return apiClient.delete<void>(`/leagues/${leagueId}/members/${memberId}`);
  },
};

// Tournament API
export interface Tournament {
  id: string;
  name: string;
  location: string | null;
  startDate: string | null;
  endDate: string | null;
  floUrl: string | null;
  wrestlestatUrl: string | null;
  weightClasses: string[];
  placePoints: number[];
  defaultBudget: number;
  tournamentType: string;
  description: string | null;
  logoUrl: string | null;
  status: string;
  defaultRosterLockTime: string | null;
  hashtags: string[];
  scrapeWrestlestatSeeds: boolean;
  finalized: boolean;
  finalizedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MatchResult {
  round: string;
  won: boolean;
  winType: string;
  opponent: string;
  opponentTeam: string;
  score: string;
  matchPoints: number;
}

export interface WrestlerMatchResults {
  matches: MatchResult[];
  matchPointsTotal: number;
  placingPoints: number;
}

export interface Wrestler {
  id: string;
  tournamentId: string;
  name: string;
  weightClass: string;
  seed: number | null;
  schoolTeam: string | null;
  place: number | null;
  teamPoints: number;
  year: string | null;
  previousPlacement: string | null;
  matchResults: WrestlerMatchResults | null;
  createdAt: string;
  updatedAt: string;
}

export interface TournamentDetails extends Tournament {
  wrestlers: Wrestler[];
  wrestlersByWeight: Record<string, Wrestler[]>;
  seedsArePreliminary: boolean;
}

export interface CreateTournamentInput {
  name: string;
  location?: string;
  startDate?: string;
  endDate?: string;
  floUrl?: string;
  wrestlestatUrl?: string;
  scrapeWrestlestatSeeds?: boolean;
  weightClasses: string[];
  placePoints: number[];
  defaultBudget?: number;
  tournamentType?: string;
  description?: string;
  logoUrl?: string;
  status?: string;
  defaultRosterLockTime?: string;
  hashtags?: string[];
}

export interface UpdateWrestlerResultsInput {
  place?: number;
  teamPoints?: number;
}

export interface AddWrestlerInput {
  name: string;
  weightClass: string;
  seed?: number;
  schoolTeam?: string;
  year?: string;
  previousPlacement?: string;
}

export interface EditWrestlerInput {
  name?: string;
  weightClass?: string;
  seed?: number | null;
  schoolTeam?: string;
  year?: string;
  previousPlacement?: string;
}

export interface UpdateTournamentInput {
  wrestlestatUrl?: string | null;
  floUrl?: string | null;
  placePoints?: number[];
  defaultBudget?: number;
  defaultRosterLockTime?: string;
  hashtags?: string[];
  scrapeWrestlestatSeeds?: boolean;
}

export const tournamentApi = {
  list: async (status?: string): Promise<Tournament[]> => {
    const url = status ? `/tournaments?status=${status}` : '/tournaments';
    return apiClient.get<Tournament[]>(url);
  },

  get: async (tournamentId: string): Promise<TournamentDetails> => {
    return apiClient.get<TournamentDetails>(`/tournaments/${tournamentId}`);
  },

  create: async (input: CreateTournamentInput): Promise<Tournament> => {
    return apiClient.post<Tournament>('/tournaments', input);
  },

  update: async (tournamentId: string, input: UpdateTournamentInput): Promise<Tournament> => {
    return apiClient.patch<Tournament>(`/tournaments/${tournamentId}`, input);
  },

  finalize: async (
    tournamentId: string,
    finalized: boolean
  ): Promise<{ id: string; finalized: boolean; finalizedAt: string | null }> => {
    return apiClient.patch(`/tournaments/${tournamentId}/finalize`, { finalized });
  },

  updateWrestlerResults: async (
    tournamentId: string,
    wrestlerId: string,
    input: UpdateWrestlerResultsInput
  ): Promise<Wrestler> => {
    return apiClient.patch<Wrestler>(`/tournaments/${tournamentId}/wrestlers/${wrestlerId}`, {
      place: input.place,
      team_points: input.teamPoints,
    });
  },

  addWrestler: async (tournamentId: string, input: AddWrestlerInput): Promise<Wrestler> => {
    return apiClient.post<Wrestler>(`/tournaments/${tournamentId}/wrestlers`, input);
  },

  editWrestler: async (
    tournamentId: string,
    wrestlerId: string,
    input: EditWrestlerInput
  ): Promise<Wrestler> => {
    return apiClient.put<Wrestler>(
      `/tournaments/${tournamentId}/wrestlers/${wrestlerId}/edit`,
      input
    );
  },

  deleteWrestler: async (tournamentId: string, wrestlerId: string): Promise<void> => {
    return apiClient.delete<void>(`/tournaments/${tournamentId}/wrestlers/${wrestlerId}`);
  },

  deleteTournament: async (tournamentId: string): Promise<void> => {
    return apiClient.delete<void>(`/tournaments/${tournamentId}`);
  },
};

// Commissioner Message API
export interface CommissionerMessage {
  id: string;
  leagueId: string;
  senderDisplayName: string | null;
  content: string;
  readAt: string | null;
  createdAt: string;
}

export const commissionerMessageApi = {
  send: async (
    leagueId: string,
    recipientMemberId: string,
    content: string
  ): Promise<CommissionerMessage> => {
    return apiClient.post<CommissionerMessage>(`/leagues/${leagueId}/commissioner-messages`, {
      recipientMemberId,
      content,
    });
  },

  getInbox: async (leagueId: string): Promise<CommissionerMessage[]> => {
    const response = await apiClient.get<{ messages: CommissionerMessage[] }>(
      `/leagues/${leagueId}/commissioner-messages/inbox`
    );
    return response.messages;
  },

  markAsRead: async (leagueId: string, messageId: string): Promise<void> => {
    return apiClient.patch<void>(
      `/leagues/${leagueId}/commissioner-messages/${messageId}/read`,
      {}
    );
  },
};

// Broadcast API
export interface Broadcast {
  id: string;
  title: string;
  content: string;
  broadcastType: 'info' | 'warning' | 'promo' | 'fun';
  publishedAt: string | null;
  expiresAt: string | null;
}

export const broadcastApi = {
  getActive: async (): Promise<Broadcast[]> => {
    const response = await apiClient.get<{ broadcasts: Broadcast[] }>('/broadcasts/active');
    return response.broadcasts;
  },

  dismiss: async (broadcastId: string): Promise<void> => {
    return apiClient.post<void>(`/broadcasts/${broadcastId}/dismiss`);
  },
};

// Roster API
export interface RosterWrestler {
  id: string;
  leagueMemberId: string;
  wrestlerId: string;
  weightClass: string;
  wrestlerCost: number;
  createdAt: string;
  wrestlerName: string;
  wrestlerSeed: number | null;
  wrestlerSchoolTeam: string | null;
  wrestlerPlace: number | null;
  wrestlerTeamPoints: number;
  wrestlerMatchResults: WrestlerMatchResults | null;
}

export interface Roster {
  leagueMemberId: string;
  wrestlers: RosterWrestler[];
  totalCost: number;
  totalPoints: number;
}

export const rosterApi = {
  get: async (leagueId: string, memberId: string): Promise<Roster> => {
    return apiClient.get<Roster>(`/leagues/${leagueId}/members/${memberId}/roster`);
  },

  selectWrestler: async (
    leagueId: string,
    memberId: string,
    wrestlerId: string
  ): Promise<RosterWrestler> => {
    return apiClient.post<RosterWrestler>(`/leagues/${leagueId}/members/${memberId}/roster`, {
      wrestler_id: wrestlerId,
    });
  },

  removeWrestler: async (
    leagueId: string,
    memberId: string,
    weightClass: string
  ): Promise<void> => {
    return apiClient.delete<void>(`/leagues/${leagueId}/members/${memberId}/roster/${weightClass}`);
  },
};
